import * as React from "react";
import { Mail, FileText, Send } from "lucide-react";
import clsx from "clsx";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { markdownSanitizeSchema } from "@/components/feature/markdown/sanitizeSchema";
import type { Artefact } from "@/modules/chat/chat-ui.types";
import { CopyActionButton } from "@/components/chat/actions/CopyActionButton";
import { useCopyAction } from "@/hooks/useCopyAction";
import { copyHtmlToClipboard } from "@/helpers/clipboard";
import { removeMarkdown } from "@/modules/chat/chat.utils";
import { ExpandButton } from "./ExpandButton";
import { InlineArtefactShell } from "./InlineArtefactShell";

interface Props {
  artefact: Artefact;
  onExpand?: (artefactId: string) => void;
}

interface LetterMeta {
  to?: string;
  from?: string;
  date?: string;
  subject?: string;
  [key: string]: string | undefined;
}

interface LetterVariant {
  label: string;
  meta: LetterMeta;
  body: string;
}

const VARIANT_SEPARATOR = /^===\s*(.+)$/m;

const parseMeta = (headerSection: string): LetterMeta => {
  const meta: LetterMeta = {};
  for (const line of headerSection.split("\n")) {
    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) continue;
    const key = line.slice(0, colonIndex).trim().toLowerCase();
    const value = line.slice(colonIndex + 1).trim();
    if (key && value) {
      meta[key] = removeMarkdown(value);
    }
  }
  return meta;
};

const parseVariantBlock = (block: string): { meta: LetterMeta; body: string } => {
  const separatorIndex = block.indexOf("\n---");
  if (separatorIndex === -1) {
    return { meta: {}, body: block.trim() };
  }
  const headerSection = block.slice(0, separatorIndex);
  const body = block.slice(separatorIndex + 4).trim();
  return { meta: parseMeta(headerSection), body };
};

const parseLetterVariants = (content: string): LetterVariant[] => {
  const parts = content.split(/^===\s*/m).filter(Boolean);

  if (parts.length <= 1 && !VARIANT_SEPARATOR.test(content)) {
    const { meta, body } = parseVariantBlock(content);
    return [{ label: "", meta, body }];
  }

  return parts.map(part => {
    const newlineIndex = part.indexOf("\n");
    if (newlineIndex === -1) {
      return { label: part.trim(), meta: {}, body: "" };
    }
    const label = part.slice(0, newlineIndex).trim();
    const rest = part.slice(newlineIndex + 1);
    const { meta, body } = parseVariantBlock(rest);
    return { label, meta, body };
  });
};

const MetaRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-2 text-sm">
    <span className="min-w-16 font-medium text-muted-foreground">{label}:</span>
    <span className="text-foreground">{value}</span>
  </div>
);

const buildMailtoHref = (meta: LetterMeta, body: string): string | null => {
  const to = meta.to && !meta.to.startsWith("[") ? meta.to : "";
  const plainBody = removeMarkdown(body);
  const parts: string[] = [];
  if (meta.subject) parts.push(`subject=${encodeURIComponent(meta.subject)}`);
  if (plainBody) parts.push(`body=${encodeURIComponent(plainBody)}`);
  const query = parts.join("&");
  return `mailto:${encodeURIComponent(to)}${query ? `?${query}` : ""}`;
};

export const LetterArtefactViewer: React.FC<Props> = React.memo(
  ({ artefact, onExpand }) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const { copied, executeCopy } = useCopyAction();

    const variants = React.useMemo(() => parseLetterVariants(artefact.content), [artefact.content]);
    const hasMultiple = variants.length > 1;
    const active = variants[activeIndex] ?? variants[0];

    const isEmail = artefact.type === "email";
    const Icon = isEmail ? Mail : FileText;

    const handleCopy = () => {
      const plain = removeMarkdown(active.body);
      const html = plain.replace(/\n/g, "<br>");
      return executeCopy(() => copyHtmlToClipboard(html, plain));
    };

    const mailtoHref = React.useMemo(
      () => (isEmail ? buildMailtoHref(active.meta, active.body) : null),
      [isEmail, active.meta, active.body],
    );

    const hasMeta = Object.values(active.meta).some(Boolean);

    return (
      <InlineArtefactShell
        name={artefact.name}
        icon={<Icon size={14} className="text-muted-foreground" />}
        headerClassName="bg-white"
        actions={
          <>
            {onExpand && <ExpandButton artefactId={artefact.id} onExpand={onExpand} />}
            <CopyActionButton copied={copied} onCopy={handleCopy} />
          </>
        }
      >
        {hasMultiple && (
          <div className="flex border-b border-desert-sand-200 bg-white">
            {variants.map((variant, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={clsx(
                  "flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors",
                  i === activeIndex
                    ? "border-b-2 border-brand-gold-500 bg-white text-brand-gold-700"
                    : "text-muted-foreground hover:bg-ivory-mist-100 hover:text-foreground",
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-desert-sand-200 text-xs font-semibold">
                  {String.fromCharCode(65 + i)}
                </span>
                {variant.label.replace(new RegExp(`^${String.fromCharCode(65 + i)}[.:;)\\-]\\s*`), "")}
              </button>
            ))}
          </div>
        )}
        <div className="bg-white p-6">
          {hasMeta && (
            <div className="mb-4 space-y-1 border-b border-desert-sand-200 pb-4">
              {active.meta.from && <MetaRow label="From" value={active.meta.from} />}
              {active.meta.to && <MetaRow label="To" value={active.meta.to} />}
              {active.meta.date && <MetaRow label="Date" value={active.meta.date} />}
              {active.meta.subject && <MetaRow label="Subject" value={active.meta.subject} />}
            </div>
          )}
          <div className="max-w-none text-sm text-foreground">
            <ReactMarkdown
              remarkPlugins={[remarkBreaks]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
              components={{
                p: ({ children }) => <p className="mb-2 whitespace-pre-wrap">{children}</p>,
              }}
            >
              {active.body}
            </ReactMarkdown>
          </div>
          {mailtoHref && (
            <div className="mt-4 flex justify-end border-t border-desert-sand-200 pt-4">
              <a
                href={mailtoHref}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                title="Open in email client"
              >
                <Send size={14} />
                Draft
              </a>
            </div>
          )}
        </div>
      </InlineArtefactShell>
    );
  },
  (prev, next) =>
    prev.artefact.id === next.artefact.id &&
    prev.artefact.content === next.artefact.content &&
    prev.onExpand === next.onExpand,
);
